import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import RaiseLabelCap from "@/pages/RaiseLabelCap";
import Marketplace from "@/pages/Marketplace";
import ProductDetail from "@/pages/ProductDetail";
import { Ranchers, RancherStorefront } from "@/pages/Ranchers";
import SellOnFarmToYou from "@/pages/SellOnFarmToYou";
import { LegalHub, LegalPolicy } from "@/pages/LegalPolicies";
import { useParams } from "react-router-dom";

function FarmRedirect() { const { id } = useParams(); return <Navigate to={`/ranchers/${id}`} replace />; }
import { Login, Register } from "@/pages/Auth";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CustomerDashboard from "@/pages/CustomerDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSupportView from "@/pages/admin/AdminSupportView";
import Warehouse from "@/pages/Warehouse";
import HowItWorks from "@/pages/HowItWorks";
import FAQ from "@/pages/FAQ";
import { Drops, DropDetail } from "@/pages/Drops";
import { Recipes, RecipeDetail } from "@/pages/Recipes"; // legacy MyRecipesPanel reused on dashboard
import MeatRecipes from "@/pages/MeatRecipes";
import LaunchBanner from "@/components/LaunchBanner";
import RestockRevealWatcher from "@/components/RestockRevealWatcher";
import WishlistWidget from "@/components/WishlistWidget";
import FoundingCountdownWidget from "@/components/FoundingCountdownWidget";
import CTOAudit from "@/pages/CTOAudit";
import AuthCallback from "@/pages/AuthCallback";
import { ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/PasswordFlows";
import AddressBook from "@/pages/AddressBook";
import { CustomerOrders, CustomerOrderDetail } from "@/pages/CustomerOrders";
import Terms from "@/pages/Terms";
import SmsConsent from "@/pages/SmsConsent";
import FoundingOffer from "@/pages/FoundingOffer";
import FoundingSuccess from "@/pages/FoundingSuccess";
import Privacy from "@/pages/Privacy";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import PartnersApply from "@/pages/PartnersApply";
import BrandPartnerApply from "@/pages/BrandPartnerApply";
import Ambassadors from "@/pages/Ambassadors";
import AmbassadorDashboard from "@/pages/AmbassadorDashboard";
import AmbassadorKit, { AmbassadorKitIndex } from "@/pages/AmbassadorKit";
import WelcomeFounder from "@/pages/WelcomeFounder";
import RefCapture, { captureRefFromQuery } from "@/lib/refTracking";
import ShareAccept from "@/pages/ShareAccept";
import ReferralLeaderboard from "@/pages/ReferralLeaderboard";
import SocialKitPage from "@/pages/SocialKitPage";
import { initSentry } from "@/lib/sentry";

initSentry();

function Protected({ children, role, roles }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="p-10 text-center text-[var(--ff-text-muted)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = roles || (role ? [role] : null);
  if (allowed && !allowed.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppShell() {
  // Detect Emergent OAuth callback synchronously during render to prevent race conditions.
  const location = useLocation();
  // Persist ?ref=CODE across page mounts (first-touch attribution, 90-day TTL).
  // MUST be declared before any conditional return to satisfy Rules of Hooks.
  useEffect(() => {
    captureRefFromQuery();
  }, [location.pathname, location.search]);
  // Scroll to top on every route change (don't fight in-page anchors / OAuth hash).
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="App">
      <div className="sticky top-0 z-50">
        <LaunchBanner />
        <Nav />
      </div>
      <RestockRevealWatcher />
      <WishlistWidget />
      <FoundingCountdownWidget />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/drops" element={<Drops />} />
        <Route path="/drops/:slug" element={<DropDetail />} />
        <Route path="/recipes" element={<MeatRecipes />} />
        <Route path="/recipes/:slug" element={<MeatRecipes />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/ranchers" element={<Ranchers />} />
        <Route path="/ranchers/:id" element={<RancherStorefront />} />
        <Route path="/sell" element={<SellOnFarmToYou />} />
        <Route path="/legal" element={<LegalHub />} />
        <Route path="/legal/:slug" element={<LegalPolicy />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/farms" element={<Navigate to="/ranchers" replace />} />
        <Route path="/farms/:id" element={<FarmRedirect />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/raise-label-cap" element={<RaiseLabelCap />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/partners/apply" element={<PartnersApply />} />
        <Route path="/partners/brand" element={<BrandPartnerApply />} />
        <Route path="/ambassadors" element={<Ambassadors />} />
        <Route path="/ambassadors/kit" element={<AmbassadorKitIndex />} />
        <Route path="/ambassadors/:slug" element={<AmbassadorKit />} />
        <Route path="/ambassador" element={<Navigate to="/ambassadors" replace />} />
        <Route path="/ambassador/dashboard" element={<Protected roles={["ambassador","admin","super_admin"]}><AmbassadorDashboard /></Protected>} />
        <Route path="/ref/:code" element={<RefCapture />} />
        <Route path="/share/accept/:token" element={<ShareAccept />} />
        <Route path="/referrals/leaderboard" element={<ReferralLeaderboard />} />
        <Route path="/social-kit" element={<Protected roles={["vendor","farm_admin","farm_staff","ambassador","admin","super_admin"]}><SocialKitPage /></Protected>} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/sms-terms" element={<SmsConsent />} />
        <Route path="/sms" element={<SmsConsent />} />
        <Route path="/founding" element={<FoundingOffer />} />
        <Route path="/founding/success" element={<FoundingSuccess />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cto-audit" element={<CTOAudit />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/welcome" element={<WelcomeFounder />} />
        <Route path="/account" element={<Protected><CustomerDashboard /></Protected>} />
        <Route path="/account/orders" element={<Protected><CustomerOrders /></Protected>} />
        <Route path="/account/orders/:id" element={<Protected><CustomerOrderDetail /></Protected>} />
        <Route path="/account/addresses" element={<Protected><AddressBook /></Protected>} />
        <Route path="/vendor" element={<Protected roles={["vendor","farm_admin","farm_staff"]}><VendorDashboard /></Protected>} />
        <Route path="/admin" element={<Protected roles={["admin","super_admin"]}><AdminDashboard /></Protected>} />
        <Route path="/admin/users/:userId" element={<Protected roles={["admin","super_admin"]}><AdminSupportView /></Protected>} />
        <Route path="/warehouse" element={<Protected roles={["admin","super_admin","warehouse_staff"]}><Warehouse /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
